import type { ComponentProps } from 'react'
import { SignIn, SignUp } from '@clerk/clerk-react'
import { useTheme } from '../theme/ThemeProvider'
import { Orb } from '../ds'

/**
 * Auth — Marva "After Hours" sign in / sign up.
 *
 * Mirrors the After Hours `web-app/Auth.html` prototype: a split layout with a
 * brand panel (left) and an auth card (right), plus the day/night theme toggle.
 *
 * The brand panel, theme toggle, and legal line are Marva-owned markup. The card
 * itself is Clerk's prebuilt <SignIn/> / <SignUp/>, themed via `appearance` to
 * match the design's inputs / social buttons / divider — so Clerk owns every auth
 * flow (email + password, email-code, Google / Apple OAuth, SSO callback) and we
 * never hand-roll the security-sensitive bits.
 *
 * Mounted in App.tsx's <SignedOut> branch at /sign-in and /sign-up (path routing,
 * so Clerk owns its own sub-steps like factor-one and sso-callback).
 */

const SunIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M12 4v2.2" /><path d="M12 17.8V20" /><path d="M5 9.5L6.5 11" /><path d="M17.5 13L19 14.5" />
    <path d="M4 14.5L5.5 13" /><path d="M18.5 11L20 9.5" /><circle cx="12" cy="12" r="3.4" />
  </svg>
)
const MoonIcon = () => (
  <svg viewBox="0 0 24 24"><path d="M21 12.8A8.5 8.5 0 1 1 11.2 3a6.6 6.6 0 0 0 9.8 9.8z" /></svg>
)

/**
 * Clerk appearance mapped onto Marva tokens. Values use CSS `var(--token)` so the
 * widget re-themes automatically when ThemeProvider flips `data-theme` on <html>.
 * Cast at the call site keeps us off a direct @clerk/types dependency.
 */
const marvaAppearance = {
  variables: {
    colorPrimary: 'var(--gold)',
    colorText: 'var(--ink)',
    colorTextSecondary: 'var(--ink-soft)',
    colorTextOnPrimaryBackground: 'var(--on-gold)',
    colorBackground: 'var(--surface)',
    colorInputBackground: 'var(--bg-2)',
    colorInputText: 'var(--ink)',
    colorDanger: 'var(--danger-ink)',
    colorNeutral: 'var(--ink)',
    borderRadius: 'var(--radius-input)',
    fontFamily: 'var(--font-sans)',
    fontFamilyButtons: 'var(--font-sans)',
    // Type sizes mirror the t-* styles (tokens.css has no size tokens): 14 = t-lead,
    // 13.5 = t-body, 12 = t-label, 11 = t-hint; the 27px title and 24px code are Clerk-only.
    fontSize: '14px',
  },
  elements: {
    rootBox: { width: '100%' },
    cardBox: { width: '100%', boxShadow: 'none', border: 'none' },
    card: { background: 'transparent', boxShadow: 'none', border: 'none', padding: 0, width: '100%' },
    headerTitle: {
      fontFamily: 'var(--font-serif)', fontWeight: 500, fontSize: '27px',
      letterSpacing: '-.4px', color: 'var(--ink)',
    },
    headerSubtitle: { color: 'var(--ink-soft)', fontSize: '13.5px' },
    socialButtonsBlockButton: {
      height: '46px', borderRadius: 'var(--radius-input)', border: '1px solid var(--line)',
      background: 'var(--surface)', color: 'var(--ink)', fontSize: '14px', fontWeight: 500,
    },
    socialButtonsBlockButtonText: { color: 'var(--ink)', fontWeight: 500 },
    dividerLine: { background: 'var(--line)' },
    dividerText: {
      color: 'var(--ink-soft)', fontSize: '11px', fontWeight: 600,
      letterSpacing: '.12em', textTransform: 'uppercase',
    },
    formFieldLabel: { color: 'var(--ink-soft)', fontWeight: 600, fontSize: '12px' },
    formFieldInput: {
      height: '46px', borderRadius: 'var(--radius-input)', background: 'var(--bg-2)',
      border: '1px solid var(--line)', color: 'var(--ink)', fontSize: '14px',
    },
    formFieldInputShowPasswordButton: { color: 'var(--ink-soft)' },
    formButtonPrimary: {
      height: '48px', borderRadius: 'var(--radius-pill)', border: 'none', textTransform: 'none',
      background: 'linear-gradient(180deg,var(--gold-soft),var(--gold))',
      color: 'var(--on-gold)', fontSize: '14.5px', fontWeight: 500,
    },
    otpCodeFieldInput: {
      borderRadius: 'var(--radius-input)', background: 'var(--bg-2)', border: '1px solid var(--line)',
      color: 'var(--ink)', fontFamily: 'var(--font-serif)', fontSize: '24px', fontWeight: 500,
    },
    footerActionText: { color: 'var(--ink-soft)' },
    footerActionLink: { color: 'var(--gold-ink)', fontWeight: 600 },
    identityPreviewText: { color: 'var(--ink)' },
    identityPreviewEditButton: { color: 'var(--gold-ink)' },
  },
} as ComponentProps<typeof SignIn>['appearance']

interface AuthPageProps {
  flow: 'sign-in' | 'sign-up'
}

const AuthPage = ({ flow }: AuthPageProps) => {
  const { theme, setMode } = useTheme()
  const isDark = theme === 'dark'

  return (
    <div className="au">
      {/* ░░░ brand panel ░░░ */}
      <aside className="au-brand">
        <div className="bgrain" />
        <div className="au-mark"><Orb size={30} float /><span className="wm">Marva</span></div>

        <div className="au-brandbody">
          <div className="eye">After Hours</div>
          <h2>The shopkeeper who <span className="em">never closes.</span></h2>
          <p>She watches your store, remembers every shopper, and reaches out at exactly the right moment — in your voice, within your limits.</p>
        </div>

        <div className="au-quote">
          <Orb size={26} />
          <div className="qt">"Even when she's acting on her own, every decision is recorded the way she'd have made it — so trusting her is never a leap of faith."
            <span className="qa">— How Marva earns the floor</span>
          </div>
        </div>
      </aside>

      {/* ░░░ auth card ░░░ */}
      <main className="au-main">
        <button
          className="au-themebtn"
          type="button"
          title="Theme"
          onClick={() => setMode(isDark ? 'day' : 'night')}
        >
          {isDark ? <MoonIcon /> : <SunIcon />}
          <span>{isDark ? 'After dark' : 'Daylight'}</span>
        </button>

        <div className="au-card">
          {flow === 'sign-up' ? (
            <SignUp appearance={marvaAppearance} routing="path" path="/sign-up" signInUrl="/sign-in" />
          ) : (
            <SignIn appearance={marvaAppearance} routing="path" path="/sign-in" signUpUrl="/sign-up" />
          )}
        </div>

        <p className="au-legal">By continuing you agree to Marva's <a href="#">Terms</a> and <a href="#">Privacy Policy</a>.</p>
      </main>
    </div>
  )
}

export default AuthPage
